import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAppNavigate } from "../lib/navigate";
import { ArrowLeft, ChevronRight, LoaderCircle } from "lucide-react";
import api from "../lib/api";
import { formatDate, formatTime } from "../lib/format";
import { formatCurrency, normalizeHexColor } from "../lib/productUtils";

const DEFAULT_PRIMARY_COLOR = "#38bdf8";

export default function OrdersHistoryPage() {
  const { tenantId = "default" } = useParams();
  const navigate = useAppNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allOrderStatuses, setAllOrderStatuses] = useState([]);
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);

  useEffect(() => {
    let active = true;

    async function loadOrders() {
      setLoading(true);
      setError("");
      try {
        const [ordersResponse, configResponse] = await Promise.allSettled([
          api.get("/webapp/orders", {
            params: { page: 1, limit: 50 },
            headers: { "x-tenant-id": tenantId },
          }),
          api.get("/webapp/config", {
            headers: { "x-tenant-id": tenantId },
          }),
        ]);

        if (ordersResponse.status !== "fulfilled") {
          throw ordersResponse.reason;
        }

        if (!active) return;

        const ordersPayload = ordersResponse.value?.data?.data;
        setOrders(Array.isArray(ordersPayload) ? ordersPayload : []);

        if (configResponse.status === "fulfilled") {
          const payload = configResponse.value?.data?.data || {};
          setAllOrderStatuses(payload.orderStatuses || []);
          setPrimaryColor(normalizeHexColor(payload.branding?.primaryColor, DEFAULT_PRIMARY_COLOR));
        } else {
          setAllOrderStatuses([]);
          setPrimaryColor(DEFAULT_PRIMARY_COLOR);
        }
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.error?.message || "Impossibile caricare lo storico ordini.");
        setOrders([]);
        setAllOrderStatuses([]);
        setPrimaryColor(DEFAULT_PRIMARY_COLOR);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadOrders();
    return () => {
      active = false;
    };
  }, [tenantId]);

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
    });
  }, [orders]);

  return (
    <div className="min-h-screen bg-[#efefef]">
      <div className="mx-auto min-h-screen w-full max-w-[800px] bg-white shadow-sm">
        <header className="sticky top-0 z-30 border-b border-[#e2e8f0] bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(`/${tenantId}`)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d7e0ea] bg-white text-[#334155]"
              aria-label="Torna allo shop"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="text-base font-semibold text-[#0f172a]">Storico ordini</h1>
            <div className="w-9" />
          </div>
        </header>

        <main className="space-y-2.5 px-4 pb-8 pt-5">
          {loading && (
            <div className="flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm text-[#64748b]">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              Caricamento ordini...
            </div>
          )}

          {error ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          ) : null}

          {!loading && !error && sortedOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-[#e2e8f0] bg-[#f8fafc] px-4 py-12 text-center">
              <p className="text-sm font-semibold text-[#0f172a]">Nessun ordine ancora presente</p>
              <p className="mt-1 text-xs text-[#64748b]">Torna al catalogo per effettuare il tuo primo ordine.</p>
              <Link
                to={`/${tenantId}`}
                className="mt-4 inline-flex h-9 items-center rounded-full px-4 text-sm font-semibold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                Torna allo shop
              </Link>
            </div>
          ) : null}

          {sortedOrders.map((order) => {
            const items = Array.isArray(order?.items) ? order.items : [];
            const itemsCount = items.reduce((acc, item) => acc + (Number(item?.quantity) || 0), 0);
            const statusDef = allOrderStatuses.find((s) => String(s._id) === String(order?.statusId));
            const statusColor = statusDef?.color || "#94a3b8";

            return (
              <button
                key={String(order._id)}
                type="button"
                onClick={() => navigate(`/${tenantId}/orders/${order._id}`)}
                className="w-full overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white px-4 py-3.5 text-left transition-colors active:bg-[#f1f5f9]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#0f172a]">
                      Ordine #{order?.orderNumber || order?._id}
                    </p>
                    <p className="mt-0.5 text-xs text-[#94a3b8]">
                      {formatDate(order?.createdAt)} alle {formatTime(order?.createdAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <div
                      className="rounded-full px-2.5 py-1 text-xs font-semibold"
                      style={{ backgroundColor: `${statusColor}20`, color: statusColor }}
                    >
                      {order?.statusName || "—"}
                    </div>
                    <ChevronRight className="h-4 w-4 text-[#cbd5e1]" />
                  </div>
                </div>
                <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-[#f1f5f9] pt-2.5">
                  <p className="text-xs text-[#94a3b8]">
                    {itemsCount} prodott{itemsCount === 1 ? "o" : "i"}
                  </p>
                  <p className="text-base font-bold text-[#0f172a]">{formatCurrency(Number(order?.total) || 0)}</p>
                </div>
              </button>
            );
          })}
        </main>
      </div>
    </div>
  );
}
